// @flow
import mqtt from 'mqtt'
import websocket from 'websocket-stream'
import { logger } from './logger'
// types
import type { Environment } from '../util/app'

/**
 * MQTT utilities
 * @module util/mqtt
 */

/**
 * Create MQTT client id
 * @param {string} env - Abbreviated environment name
 * @param {string} userId - User Id
 * @returns {string} - Client id
 */
export const createClientId = (env: Environment, userId: string) => {
  if(!userId){
    logger.error(new Error('MISSING_USER_ID'))
  }
  
  return `web3-${env}-${Math.floor(Math.random() * 10000)}-${userId}`
}

/**
 * Create MQTT topic id
 * @function
 * @param {string} userId - User Id
 * @returns {string} - Topic id
 */
export const createTopicId = (userId: string) => `rachio/person/${userId}/event`

/**
 * Create MQTT client and subscribes to `topicId`
 * @param {string} url - Url to connect to
 * @param {string} topicId - Topic to subscribe to once connected
 * @param {string} clientId - Client ID
 * @returns {object} - Mqtt client
 */
export const createMqttClient = (
  url: string,
  topicId: string,
  clientId: string
) => {
  const ws = websocket(url, ['mqttv3.1'])
  const client = new mqtt.MqttClient(() => ws, {
    clientId: clientId,
    keepalive: 10000
  })

  // Give us infinite listeners
  ws.setMaxListeners(0)

  ws.socket.addEventListener('open', () => {
    logger.info('wsOpen')
  })
  ws.socket.addEventListener('message', msg => {
    logger.info('wsMessage', msg)
  })
  ws.socket.addEventListener('close', (code, reason) => {
    logger.info('wsClose', { code, reason })
  })
  ws.socket.addEventListener('error', err => {
    logger.warn('WS_ERROR', { error: err, ws: ws, client })
  })

  client.on('connect', () => {
    logger.info('MQTT Client Connected')
    logger.info(`Subscribing to: ${topicId}`)
    client.subscribe(topicId)
  })

  client.on('error', err =>
    logger.error(new Error('MQTT_CLIENT_ERROR'), { error: err, client })
  )

  return client
}

export default createMqttClient
